import { doc, updateDoc, deleteDoc, collection, addDoc, getDocs, query, where, getDoc, serverTimestamp, orderBy } from 'firebase/firestore';
import { db } from '../config/firebase';
import { getFunctions, httpsCallable } from 'firebase/functions';
import { auth } from '../config/firebase';

export const updateBank = async (bankId, updates) => {
  const bankRef = doc(db, 'banks', bankId);
  await updateDoc(bankRef, updates);
  return true;
};

export const deleteBank = async (bankId) => {
  const bankRef = doc(db, 'banks', bankId);
  await deleteDoc(bankRef);
  return true;
};

// Dismiss walkthrough for user
export const dismissWalkthrough = async (uid) => {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, {
    hasSeenWalkthrough: true
  });
  return true;
};

// Reset walkthrough for user (allows them to see it again)
export const resetWalkthrough = async (uid) => {
  const userRef = doc(db, 'users', uid);
  await updateDoc(userRef, {
    hasSeenWalkthrough: false
  });
  return true;
};

export const sendInviteEmail = async (data) => {
  try {
    // First, store the invite in Firestore
    const invitesRef = collection(db, 'pendingInvites');
    await addDoc(invitesRef, {
      toEmail: data.toEmail,
      fromUserId: data.fromUserId,
      fromEmail: data.fromEmail,
      status: 'pending',
      createdAt: new Date().toISOString(),
    });

    // Call the Cloud Function to send the email
    const functions = getFunctions();
    const sendInvite = httpsCallable(functions, 'sendInviteEmail');
    const result = await sendInvite(data);

    return result.data;
  } catch (error) {
    console.error('Error sending invite:', error);
    throw error;
  }
};

// Friend-related functions
export const getFriends = async (userId) => {
  try {
    const friends = new Map(); // Use Map to avoid duplicates

    // Helper function to get user's total earned from bonuses
    const getUserTotalEarned = async (uid) => {
      try {
        const userBonusesRef = collection(db, 'userBonuses');
        const q = query(userBonusesRef, where('userId', '==', uid));
        const snapshot = await getDocs(q);

        let total = 0;
        snapshot.forEach(doc => {
          const data = doc.data();
          if (data.status === 'completed' && data.amount) {
            total += parseFloat(data.amount) || 0;
          }
        });

        return total;
      } catch (error) {
        console.error('Error getting user total earned:', error);
        return 0;
      }
    };

    // Get friends from the friends collection (new structure)
    const friendsRef = collection(db, 'friends');
    const friendsQuery = query(friendsRef,
      where('users', 'array-contains', userId),
      where('status', '==', 'accepted')
    );

    const friendsSnapshot = await getDocs(friendsQuery);

    // Process friends from the friends collection
    for (const docSnap of friendsSnapshot.docs) {
      const friendData = docSnap.data();
      const otherUserId = friendData.users.find(id => id !== userId);
      if (otherUserId && !friends.has(otherUserId)) {
        const userDocRef = doc(db, 'users', otherUserId);
        const userDocSnap = await getDoc(userDocRef);
        const userData = userDocSnap.data();

        if (userData) {
          const totalEarned = await getUserTotalEarned(otherUserId);

          friends.set(otherUserId, {
            id: otherUserId,
            displayName: userData.displayName || userData.email?.split('@')[0],
            email: userData.email,
            joinDate: userData.createdAt?.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt),
            totalEarned: totalEarned,
          });
        }
      }
    }

    // Get friends from user's friends subcollection (old structure)
    const userFriendsRef = collection(db, 'users', userId, 'friends');
    const userFriendsSnapshot = await getDocs(userFriendsRef);

    // Process friends from the user's friends subcollection
    for (const docSnap of userFriendsSnapshot.docs) {
      const friendData = docSnap.data();
      const friendId = friendData.userId;

      if (friendId && !friends.has(friendId)) {
        const userDocRef = doc(db, 'users', friendId);
        const userDocSnap = await getDoc(userDocRef);
        const userData = userDocSnap.data();

        if (userData) {
          const totalEarned = await getUserTotalEarned(friendId);

          friends.set(friendId, {
            id: friendId,
            displayName: userData.displayName || userData.email?.split('@')[0],
            email: userData.email,
            joinDate: userData.createdAt?.toDate ? userData.createdAt.toDate() : new Date(userData.createdAt),
            totalEarned: totalEarned,
          });
        }
      }
    }

    return Array.from(friends.values());
  } catch (error) {
    console.error('Error getting friends:', error);
    throw error;
  }
};

export const getPendingRequests = async (userId) => {
  const requestsRef = collection(db, 'friendRequests');
  const q = query(requestsRef, 
    where('fromUserId', '==', userId),
    where('status', '==', 'pending')
  );
  
  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
};

export const getIncomingRequests = async (userId) => {
  try {
    // Query the friendRequests collection for incoming requests
    const requestsRef = collection(db, 'friendRequests');
    const q = query(
      requestsRef, 
      where('toUserId', '==', userId),
      where('status', '==', 'pending'),
      orderBy('createdAt', 'desc')
    );
    
    const snapshot = await getDocs(q);
    return snapshot.docs.map(doc => ({
      id: doc.id,
      ...doc.data(),
      createdAt: doc.data().createdAt?.toDate?.() || new Date(),
    }));
  } catch (error) {
    console.error('Error getting incoming requests:', error);
    throw error;
  }
};

export async function sendFriendRequest(toEmail) {
  try {
    if (!toEmail || typeof toEmail !== 'string') {
      throw new Error("Invalid email address");
    }

    if (!auth.currentUser?.uid) {
      throw new Error("You must be logged in to send friend requests");
    }

    if (toEmail === auth.currentUser.email) {
      throw new Error("You cannot send a friend request to yourself");
    }

    // Query for user with the given email
    const usersRef = collection(db, "users");
    const q = query(usersRef, where("email", "==", toEmail.toLowerCase()));
    const querySnapshot = await getDocs(q);
    
    if (querySnapshot.empty) {
      // User doesn't exist - create a pending invite
      const pendingInvitesRef = collection(db, "pendingInvites");
      const inviteDoc = await addDoc(pendingInvitesRef, {
        toEmail: toEmail.toLowerCase(),
        fromUserId: auth.currentUser.uid,
        fromUserEmail: auth.currentUser.email,
        status: "pending",
        createdAt: serverTimestamp()
      });

      try {
        // Send invitation email using Firebase Functions
        const functions = getFunctions();
        const sendInviteEmail = httpsCallable(functions, 'sendInviteEmail');
        await sendInviteEmail({ 
          toEmail: toEmail.toLowerCase(), 
          fromEmail: auth.currentUser.email 
        });
      } catch (emailError) {
        console.error("Error sending invite email:", emailError);
      }

      return { 
        success: true, 
        type: "invite_sent",
        inviteId: inviteDoc.id
      };
    }

    // User exists - send friend request
    const toUserId = querySnapshot.docs[0].id;
    const toUserData = querySnapshot.docs[0].data();
    
    // Check if request already exists in friendRequests collection
    const requestsRef = collection(db, "friendRequests");
    const existingRequestQuery = query(
      requestsRef,
      where("fromUserId", "==", auth.currentUser.uid),
      where("toUserId", "==", toUserId),
      where("status", "==", "pending")
    );
    const existingRequests = await getDocs(existingRequestQuery);
    
    if (!existingRequests.empty) {
      throw new Error("A friend request to this user is already pending");
    }

    // Check if they're already friends
    const friendsRef = collection(db, "friends");
    const existingFriendQuery = query(
      friendsRef,
      where("users", "array-contains", auth.currentUser.uid),
      where("status", "==", "accepted")
    );
    const existingFriends = await getDocs(existingFriendQuery);
    
    for (const doc of existingFriends.docs) {
      const friendData = doc.data();
      if (friendData.users.includes(toUserId)) {
        throw new Error("You are already friends with this user");
      }
    }

    // Add friend request to friendRequests collection
    await addDoc(requestsRef, {
      fromUserId: auth.currentUser.uid,
      fromEmail: auth.currentUser.email,
      fromName: auth.currentUser.displayName,
      toUserId: toUserId,
      toEmail: toUserData.email,
      toName: toUserData.displayName,
      status: "pending",
      createdAt: serverTimestamp()
    });

    return { 
      success: true, 
      type: "request_sent" 
    };
  } catch (error) {
    console.error("Error sending friend request:", error);
    throw error;
  }
}

export const acceptFriendRequest = async (requestId) => {
  const requestRef = doc(db, 'friendRequests', requestId);
  const requestDoc = await getDoc(requestRef);
  const requestData = requestDoc.data();

  // Create the friend relationship
  const friendsRef = collection(db, 'friends');
  await addDoc(friendsRef, {
    users: [requestData.fromUserId, requestData.toUserId],
    status: 'accepted',
    createdAt: new Date().toISOString(),
  });

  // Update the request status
  await updateDoc(requestRef, {
    status: 'accepted',
    updatedAt: new Date().toISOString(),
  });
};

export const rejectFriendRequest = async (requestId) => {
  const requestRef = doc(db, 'friendRequests', requestId);
  await updateDoc(requestRef, {
    status: 'rejected',
    updatedAt: new Date().toISOString(),
  });
};

export const removeFriend = async (currentUserId, friendUserId) => {
  try {
    // Remove from the friends collection (new structure)
    const friendsRef = collection(db, 'friends');
    const friendsQuery = query(
      friendsRef,
      where('users', 'array-contains', currentUserId),
      where('status', '==', 'accepted')
    );

    const friendsSnapshot = await getDocs(friendsQuery);
    for (const docSnap of friendsSnapshot.docs) {
      const friendData = docSnap.data();
      if (friendData.users.includes(friendUserId)) {
        await deleteDoc(doc(db, 'friends', docSnap.id));
      }
    }

    // Remove from user's friends subcollection (old structure)
    const currentUserFriendRef = collection(db, 'users', currentUserId, 'friends');
    const currentUserFriendSnapshot = await getDocs(currentUserFriendRef);
    for (const docSnap of currentUserFriendSnapshot.docs) {
      if (docSnap.data().userId === friendUserId) {
        await deleteDoc(doc(db, 'users', currentUserId, 'friends', docSnap.id));
      }
    }

    // Remove from friend's friends subcollection (old structure)
    const friendUserFriendRef = collection(db, 'users', friendUserId, 'friends');
    const friendUserFriendSnapshot = await getDocs(friendUserFriendRef);
    for (const docSnap of friendUserFriendSnapshot.docs) {
      if (docSnap.data().userId === currentUserId) {
        await deleteDoc(doc(db, 'users', friendUserId, 'friends', docSnap.id));
      }
    }

    return { success: true };
  } catch (error) {
    console.error('Error removing friend:', error);
    throw error;
  }
}; 